import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check, RefreshCw, Search, AlertCircle } from "lucide-react";
import { useAuth } from "../../AuthContext";
import { BACKEND_URL } from "../../lib/backend";
import { ProposalFields } from "./ProposalFields";
import { MyProposals } from "./MyProposals";

const STAGES = ["Reading the organiser's page…", "Extracting name, dates and venue…", "Checking the catalogue for duplicates…"];
const EMPTY = { name: "", organiser: "", start_date: "", end_date: "", city: "", country: "", venue: "", event_type: "", access: "", themes: [] };

function ErrorNote({ err, c }) {
  if (!err) return null;
  return (
    <div data-testid="propose-error" className={`mt-3 flex items-start gap-2 rounded-xl px-3 py-2.5 text-sm ${err.code === "in_catalog" || err.code === "already_proposed" ? (c.tagPage) : "bg-rose-500/10 text-rose-500"}`}>
      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span>
        {err.message}
        {err.event?.web_url && <> <Link to={err.event.web_url} data-testid="propose-error-event-link" className="underline font-semibold">Open {err.event.name}</Link></>}
        {err.code === "in_catalog" && !err.event?.web_url && err.event?.name && <> ({err.event.name} — awaiting publication)</>}
      </span>
    </div>
  );
}

/** URL → analyse → fields → submit. Every required field must be filled before sending. */
export function ProposeEventForm({ dm, c, autoFocus }) {
  const { authHeader } = useAuth();
  const [url, setUrl] = useState("");
  const [step, setStep] = useState("url"); // url | analysing | fields | done
  const [stage, setStage] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [fields, setFields] = useState(EMPTY);
  const [prov, setProv] = useState({});
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => { axios.get(`${BACKEND_URL}/api/events/proposals/meta`).then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  useEffect(() => { if (autoFocus && inputRef.current) inputRef.current.focus(); }, [autoFocus]);
  useEffect(() => {
    if (step !== "analysing") return undefined;
    setStage(0);
    const t = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), 6000);
    return () => clearInterval(t);
  }, [step]);

  const fail = (e) => {
    const body = e?.response?.data;
    const d = body?.code ? body : body?.detail;
    setErr(typeof d === "object" && d ? d : { code: "error", message: typeof d === "string" ? d : "Something went wrong — please try again." });
  };

  const analyse = async (e) => {
    e.preventDefault();
    setErr(null); setStep("analysing");
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/events/proposals/analyse`, { url: url.trim() }, { headers: authHeader() });
      setAnalysis(data); setFields({ ...EMPTY, ...data.fields }); setProv(data.provenance || {}); setStep("fields");
    } catch (ex) { fail(ex); setStep("url"); }
  };

  const setField = useCallback((k, v) => {
    setFields((f) => ({ ...f, [k]: v }));
    setProv((p) => ({ ...p, [k]: "user" }));
  }, []);

  const missing = Object.keys(EMPTY).filter((k) => (Array.isArray(fields[k]) ? fields[k].length === 0 : !fields[k]));
  const submit = async (e) => {
    e.preventDefault();
    if (missing.length) return;
    setSaving(true); setErr(null);
    try {
      await axios.post(`${BACKEND_URL}/api/events/proposals`, { url: analysis.url, fields, provenance: prov, analysis: { page_kind: analysis.page_kind, date_quote: analysis.date_quote } }, { headers: authHeader() });
      setStep("done"); setRefreshKey((k) => k + 1);
    } catch (ex) { fail(ex); }
    setSaving(false);
  };
  const reset = () => { setUrl(""); setAnalysis(null); setFields(EMPTY); setProv({}); setErr(null); setStep("url"); };

  return (
    <div className="mt-5" data-testid="propose-event-form">
      {(step === "url" || step === "analysing") && (
        <form onSubmit={analyse} className="flex flex-col sm:flex-row gap-2">
          <input ref={inputRef} type="url" required value={url} onChange={(e) => { setUrl(e.target.value); setErr(null); }} disabled={step === "analysing"}
            placeholder="https://www.organiser.com/event-2027" data-testid="propose-url-input"
            className={`flex-1 h-10 rounded-lg border px-3 text-sm outline-none focus:ring-2 focus:ring-[#006874]/30 ${c.input}`} />
          <button type="submit" disabled={step === "analysing" || !/^https?:\/\/.+\..+/.test(url.trim())} data-testid="propose-analyse-btn"
            className={`inline-flex items-center justify-center gap-2 px-5 h-10 rounded-full text-sm font-semibold whitespace-nowrap transition-colors disabled:opacity-50 ${c.btn}`}>
            {step === "analysing" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}{step === "analysing" ? "Analysing…" : "Analyse the page"}
          </button>
        </form>
      )}
      {step === "analysing" && (
        <div className="mt-3" data-testid="propose-progress" aria-live="polite">
          <div className={`h-1.5 rounded-full overflow-hidden ${dm ? "bg-white/8" : "bg-[#e7e8e9]"}`}>
            <div className={`h-full rounded-full ${dm ? "bg-cyan-400" : "bg-[#006874]"} transition-[width] duration-[6000ms] ease-linear`} style={{ width: `${((stage + 1) / (STAGES.length + 1)) * 100}%` }} />
          </div>
          <p className={`mt-1.5 text-xs ${c.muted}`}>{STAGES[stage]} <span className="opacity-70">(10–20 seconds)</span></p>
        </div>
      )}
      <ErrorNote err={err} c={c} />

      {step === "fields" && analysis && (
        <form onSubmit={submit} className="mt-2">
          <div className={`rounded-xl px-3 py-2.5 text-sm flex items-start gap-2 ${analysis.readable ? c.tagPage : c.tagUser}`} data-testid="propose-analysis-note">
            {analysis.readable ? <Check className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
            <span>
              {!analysis.readable && "We couldn't read this page (blocked, PDF or JavaScript-only). Fill in the details yourself — the URL stays as the official page."}
              {analysis.readable && missing.length === 0 && "Everything was on the page — check the details and confirm."}
              {analysis.readable && missing.length > 0 && `Found ${Object.keys(EMPTY).length - missing.length} of ${Object.keys(EMPTY).length} details on the page — please add the ${missing.length} missing one${missing.length !== 1 ? "s" : ""}.`}
            </span>
          </div>
          <p className={`mt-3 text-xs ${c.muted}`}>Official page: <a href={analysis.url} target="_blank" rel="noopener noreferrer" className={`underline ${c.accent}`} data-testid="propose-official-url">{analysis.url}</a></p>
          <ProposalFields fields={fields} prov={prov} setField={setField} taxonomy={analysis.taxonomy || meta?.taxonomy} c={c} dm={dm} />
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button type="submit" disabled={saving || missing.length > 0} data-testid="propose-submit-btn"
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-50 ${c.btn}`}>
              {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}{missing.length === 0 ? "Send for review" : `${missing.length} field${missing.length !== 1 ? "s" : ""} still missing`}
            </button>
            <button type="button" onClick={reset} data-testid="propose-cancel-btn" className={`text-xs underline ${c.muted}`}>Start over</button>
          </div>
        </form>
      )}

      {step === "done" && (
        <div data-testid="propose-success" className={`mt-2 rounded-xl px-4 py-3 flex items-start gap-3 ${c.tagPage}`}>
          <span className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0"><Check className="w-3.5 h-3.5 text-white" /></span>
          <div className="text-sm">
            <p className="font-semibold">Thanks! Your proposal is in review by our editors.</p>
            <p className="opacity-80 mt-0.5">We&apos;ll email you when it&apos;s published — or explain why it wasn&apos;t accepted. <button type="button" onClick={reset} className="underline" data-testid="propose-another-btn">Propose another</button></p>
          </div>
        </div>
      )}
      <MyProposals c={c} refreshKey={refreshKey} />
    </div>
  );
}
